/**
 * ArtifactErrorBoundary - Error Isolation for Artifacts
 * TASK-286, TASK-289: Agentic Chat Artifact Rendering
 *
 * Catches render errors in artifacts to prevent crashing the entire chat.
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  artifactId: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ArtifactErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`Artifact ${this.props.artifactId} render error:`, error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-700 dark:text-red-400">
                Failed to render artifact
              </h4>
              <p className="text-xs text-red-600 dark:text-red-300 mt-1">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
              <button
                onClick={this.handleRetry}
                className="mt-2 inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:underline"
              >
                <RefreshCw className="w-3 h-3" />
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ArtifactErrorBoundary;
